import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ChangeDetectorRef,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  ButtonsModule,
  CardModule,
  CheckboxModule,
  LoadingService,
} from 'axis-components';

import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { StateSummaryService } from '../../../_services/pip/state-summary.service';
import { RequestService } from '../../../_services/request/request.service';
import { UserService } from '../../../_services/user.service';
import { FormType, RequestProgressStep } from '../../../config/constants';
import { PipBaseRulesService } from '../../../state-disclosures/pip/services/pip-base-rules.service';
import { PipBaseStateComponent } from '../../../state-disclosures/pip/pip-base-state.component';
import { BrowserNavigationService } from '../../../_services/browser-navigation.service';
@Component({
  selector: 'app-pip-state-summary',
  templateUrl: './pip-state-summary.component.html',
  styleUrls: ['./pip-state-summary.component.scss'],
  standalone: true,
  imports: [
    ButtonsModule,
    CardModule,
    CommonModule,
    CheckboxModule,
    ReactiveFormsModule,
  ],
})
export class PipStateSummaryComponent implements OnInit, AfterViewInit {
  public pipStateSummary: FormGroup;
  @ViewChild('stateComponent', { read: ViewContainerRef })
  stateComponent!: ViewContainerRef;
  @ViewChild('summaryContainer', { read: ElementRef })
  summaryContainer!: ElementRef;
  public baseRulesService = inject(PipBaseRulesService);

  // Events emitted to playground component for workflow navigation
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();

  isBroker = false;
  constructor(
    private readonly stateSummaryService: StateSummaryService,
    private router: Router,
    private fb: FormBuilder,
    private requestService: RequestService,
    private userService: UserService,
    private loadingService: LoadingService,
    private cdr: ChangeDetectorRef,
    private browserNavigationService: BrowserNavigationService,
  ) {}
  stateSummaryData: any;
  ngOnInit(): void {
    this.browserNavigationService.startListening({
      onBackNavigation: () => PipBaseStateComponent.resetLoadingState(),
    });
    PipBaseStateComponent.resetLoadingState();
    this.loadingService.start('State Summary is Loading');
    this.isBroker = this.userService.role === 'broker';

    this.pipStateSummary = this.fb.group({
      pipConfirmStateDis: [''],
    });
    this.stateSummaryService.getStateSummaryData().subscribe((data) => {
      this.stateSummaryData = data;
    });

    // Restore checkbox state from dataDump (checkbox retention)
    const savedCheckbox =
      this.baseRulesService.dataDump?.['summaryConfirmation'];
    if (savedCheckbox !== undefined) {
      this.pipStateSummary.patchValue({ pipConfirmStateDis: savedCheckbox });
    }

    // Subscribe to form changes to save checkbox state
    this.pipStateSummary.valueChanges.subscribe(() => {
      this.saveCheckboxState();
    });

    // Set up validation function for sidebar navigation
    this.baseRulesService.canNavigateFromSummary = () => {
      return this.validateSummaryForm();
    };

    this.goTop();
  }

  ngOnDestroy(): void {
    this.browserNavigationService.stopListening();
  }

  ngAfterViewInit(): void {
    this.setupDynamicComponents();
  }

  setupDynamicComponents() {
    let applicableStates = this.baseRulesService.getApplicableStates();
    applicableStates.forEach(({ state, stateComponent }) => {
      const stateComponentRef =
        this.stateComponent.createComponent(stateComponent);

      (stateComponentRef.instance as any).isReview = true;
    });
    this.waitForFinalRender();
    // this is temporary solution until we get all the json compiled into 1 json for all states from backend
    setTimeout(() => {
      console.log('Is state page empty : ', this.isStatePageEmptyScoped);
    }, 1000);
  }

  private waitForFinalRender(): void {
    const checkUIState = () => {
      if (PipBaseStateComponent.stopLoading) {
        this.loadingService.stop();
        return;
      } else {
        this.cdr.detectChanges();
        requestAnimationFrame(checkUIState);
      }
    };
    requestAnimationFrame(checkUIState);
  }

  get isStatePageEmptyScoped(): boolean {
    if (this.summaryContainer?.nativeElement) {
      return this.baseRulesService.isStatePageEmpty(
        this.summaryContainer.nativeElement,
      );
    }
    return this.baseRulesService.isStatePageEmpty();
  }

  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }
  onBack(): void {
    // Reset loading state when going back
    PipBaseStateComponent.resetLoadingState();
    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.backClickedPlayground.emit();
      return;
    }
    this.router.navigate(['/insured/pip-selection'], {
      queryParamsHandling: 'preserve',
    });
  }
  onContinue(): void {
    // Validate the summary form
    if (!this.validateSummaryForm()) {
      return;
    }

    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.continueClickedPlayground.emit();
      return;
    }

    this.requestService.refreshRequestData(false).subscribe({
      next: () => {
        this.handleContinueWithFreshStep();
      },
      error: () => {
        this.handleContinueWithFreshStep();
      },
    });
  }

  private handleContinueWithFreshStep(): void {
    // Read current DB-backed step snapshot before moving the form ahead.
    const isPipCompleted = this.requestService.isFormTypeAlreadyCompleted(
      FormType.PIP,
    );

    // If PIP is already completed, do not continue with consent routing.
    if (isPipCompleted) {
      return;
    }

    this.requestService.updateRequestProgress(
      FormType.PIP,
      RequestProgressStep.REVIEW_STATE_SELECTIONS,
      this.baseRulesService.dataDump,
    );

    this.router.navigate(['/insured/pip-outstanding-state'], {
      queryParamsHandling: 'preserve',
    });
  }

  private validateSummaryForm(): boolean {
    // Check if page is empty and update validators accordingly
    const isEmpty = this.isStatePageEmptyScoped;
    const confirmControl = this.pipStateSummary.get('pipConfirmStateDis');

    if (isEmpty || this.isBroker) {
      // If page is empty or user is broker, remove validators and clear the control
      confirmControl?.clearValidators();
      confirmControl?.setValue('');
    } else {
      // If page has content and user is not broker, require the checkbox
      confirmControl?.setValidators([Validators.requiredTrue]);
    }

    // Update validation state
    confirmControl?.updateValueAndValidity();
    confirmControl?.markAsTouched();

    if (this.pipStateSummary.invalid && !isEmpty && !this.isBroker) {
      console.log('Form is invalid');
      return false;
    }

    return true;
  }

  /**
   * Save checkbox state to dataDump for persistence
   */
  private saveCheckboxState(): void {
    const checkboxValue = this.pipStateSummary.get('pipConfirmStateDis')?.value;
    // Initialize dataDump as object if needed (it can be array or object)
    if (
      !this.baseRulesService.dataDump ||
      Array.isArray(this.baseRulesService.dataDump)
    ) {
      this.baseRulesService.dataDump = this.baseRulesService.dataDump || [];
    }
    this.baseRulesService.dataDump['summaryConfirmation'] = checkboxValue;
  }
}
